import i18n, { currentLocale } from '@/i18n';
import { tryParseDateOnly } from '@/lib/date';
import type { AdminCrawler, AdminEvent } from '@/types/admin';

function formatSpecificDate(value: string): string {
  const date = tryParseDateOnly(value);
  return date ? new Intl.DateTimeFormat(currentLocale(), {
    year: 'numeric', month: 'short', day: 'numeric',
  }).format(date) : value;
}

export function formatAdminEventDate(event: AdminEvent): string {
  if (event.event_type === 'specific') {
    if (!event.start_date) {
      return i18n.t('admin:date.pending');
    }
    if (event.end_date) {
      return `${formatSpecificDate(event.start_date)} ~ ${formatSpecificDate(event.end_date)}`;
    }
    return formatSpecificDate(event.start_date);
  }
  if (!event.month || !event.day) {
    return i18n.t('admin:date.pending');
  }
  const date = new Intl.DateTimeFormat(currentLocale(), {
    month: 'long', day: 'numeric',
  }).format(new Date(2000, event.month - 1, event.day));
  const annual = i18n.t('admin:date.annual', { date });
  if (event.year) {
    return i18n.t('admin:date.annualYear', { date: annual, year: event.year });
  }
  return annual;
}

export function crawlerName(crawler: Pick<AdminCrawler, 'id' | 'name'>): string {
  return i18n.t(`admin:crawlers.names.${crawler.id}`, { defaultValue: crawler.name });
}

const CRAWLER_ERROR_KEYS: Record<string, string> = {
  '运行超时或进程中断': 'expired',
  '运行已超时或结束，不能再修改活动数据': 'expired',
  '未知错误': 'unknown',
  '缺少 FIRECRAWL_API_KEY；请配置 Firecrawl API Key': 'missingKey',
  'FIRECRAWL_API_KEY 格式无效': 'invalidKey',
  'Firecrawl 未能完成页面抓取': 'incomplete',
  'Firecrawl 返回了无效的页面数据': 'invalidData',
  'Firecrawl 抓取的源站返回错误；页面可能被拦截或暂时不可用': 'sourceError',
  'Firecrawl 未返回有效的 HTML 页面': 'invalidHtml',
  '爬虫来源 URL 无效': 'invalidUrl',
  '爬虫来源 URL 或查询参数无效': 'invalidUrl',
  'Firecrawl 页面抓取超时': 'timeout',
  '无法连接 Firecrawl': 'connection',
  'Firecrawl 抓取请求失败': 'request',
  'Firecrawl 额度不足（HTTP 402），请检查账户额度': 'credits',
  'Firecrawl 速率受限（HTTP 429），请稍后重试': 'rateLimit',
  'Firecrawl 返回了无效的 JSON 响应': 'invalidJson',
  '无法获取网页内容': 'noContent',
  '未解析到航展活动；页面可能被拦截、未加载完整或结构已变更': 'noEvents',
  '缺少 CLOUDFLARE_ACCOUNT_ID 或 CLOUDFLARE_API_TOKEN；未配置 Cloudflare Browser Run': 'browserRunMissingKey',
  'CLOUDFLARE_ACCOUNT_ID 或 CLOUDFLARE_API_TOKEN 格式无效': 'browserRunInvalidKey',
  '详情页 URL 无效': 'browserRunInvalidUrl',
  'Cloudflare Browser Run 请求超时': 'browserRunTimeout',
  '无法连接 Cloudflare Browser Run': 'browserRunConnection',
  'Cloudflare Browser Run 请求失败': 'browserRunRequest',
  'Cloudflare Browser Run 速率受限或当日额度用尽（HTTP 429）': 'browserRunExhausted',
  'Cloudflare Browser Run 返回了无效的 JSON 响应': 'browserRunInvalidJson',
  'Cloudflare Browser Run 未能完成页面抓取': 'browserRunIncomplete',
  'Cloudflare Browser Run 未返回有效的 HTML 页面': 'browserRunInvalidHtml',
};

// 详情页取名任务在 Browser Run 出现服务级故障时回退 Firecrawl，运行错误带这个后缀。
const FALLBACK_SUFFIX = '；已回退 Firecrawl';

/** Localize known service diagnostics while preserving unexpected source details. */
export function formatCrawlerError(error: string | null): string {
  if (!error) return i18n.t('admin:crawlers.unknownError');
  if (error.endsWith(FALLBACK_SUFFIX)) {
    return i18n.t('admin:crawlers.errors.browserRunFallback', {
      reason: formatCrawlerError(error.slice(0, -FALLBACK_SUFFIX.length)),
    });
  }
  const key = CRAWLER_ERROR_KEYS[error];
  if (key) return i18n.t(`admin:crawlers.errors.${key}`);
  const patterns: Array<[RegExp, string, string]> = [
    [/^Firecrawl 抓取的源站返回 HTTP (\d+(?:\.\d+)?)；页面可能被拦截或暂时不可用$/, 'sourceStatus', 'status'],
    [/^Firecrawl 认证或授权失败（HTTP (\d+)）；请检查 FIRECRAWL_API_KEY$/, 'auth', 'status'],
    [/^Firecrawl 服务暂时不可用（HTTP (\d+)）$/, 'unavailable', 'status'],
    [/^Firecrawl 抓取请求失败（HTTP (\d+)）$/, 'requestStatus', 'status'],
    [/^Cloudflare Browser Run 认证或授权失败（HTTP (\d+)）；请检查 CLOUDFLARE_API_TOKEN$/, 'browserRunAuth', 'status'],
    [/^Cloudflare Browser Run 服务暂时不可用（HTTP (\d+)）$/, 'browserRunUnavailable', 'status'],
    [/^Cloudflare Browser Run 未能打开页面（HTTP (\d+)）$/, 'browserRunPage', 'status'],
    [/^(\d+) 个活动行全部无法解析$/, 'unparsed', 'count'],
    [/^爬虫不存在: (.+)$/, 'notFound', 'id'],
    [/^爬虫已禁用: (.+)$/, 'disabled', 'id'],
  ];
  for (const [pattern, message, field] of patterns) {
    const match = error.match(pattern);
    if (match) return i18n.t(`admin:crawlers.errors.${message}`, {
      [field]: field === 'count' ? Number(match[1]) : match[1],
    });
  }
  return error;
}
